import { useRef } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import { usePagePermissions } from '@/contexts/PagePermissionsContext';
import { Spinner } from '@/components/ui/EmptyState';
import { matchPageKey } from '@/pages/admin/nav';

// Gate every /admin/* route on: signed in, has a role with at least one
// permitted rxk page (or is the Founder, who always bypasses), and — for
// routes that map to a specific sidebar page — that page is in their
// role's allowed set. Unauthenticated -> home (carries the intended path
// via location state so Home can send the user straight back here after
// they sign in). No access at all, or this specific page isn't allowed ->
// redirected rather than shown a blocked page, since the sidebar already
// never linked here for them.
export function RequirePagePermission({ children }: { children: ReactNode }) {
  const { role, loading: roleLoading, user } = useUserRole();
  const { loading: permsLoading, isFounder, hasPortalAccess, allowedPages } = usePagePermissions();
  const location = useLocation();
  // Once permissions have loaded successfully here, a LATER loading flip
  // (role/perms context re-checking after any auth event — e.g. the silent
  // TOKEN_REFRESHED Supabase fires when a background tab regains focus)
  // must never fall back to the Spinner branch again: that branch renders
  // in place of {children}, which unmounts the entire routed page — and
  // anything open inside it, like NewOrderModal mid-scan — for no reason
  // other than a background re-check of something that hasn't actually
  // changed. Only the true first load ever shows the spinner; every
  // subsequent pass falls through using whatever role/user/allowedPages the
  // contexts already have (never cleared before a re-check completes).
  const hasLoadedOnce = useRef(false);
  const showSpinner = (roleLoading || permsLoading) && !hasLoadedOnce.current;
  if (showSpinner) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!roleLoading && !permsLoading) hasLoadedOnce.current = true;
  if (!user) return <Navigate to="/" replace state={{ from: location }} />;
  if (isFounder) return <>{children}</>;
  // The Operations Hub's Team > Portal Access matrix is the actual front
  // door — a role having rxk_role_permissions rows never used to matter if
  // the hub itself never granted this specific person the 'rxk' portal.
  if (!hasPortalAccess) return <Navigate to="/" replace />;
  if (!role || allowedPages.size === 0) return <Navigate to="/" replace />;

  const pageKey = matchPageKey(location.pathname);
  if (pageKey && !allowedPages.has(pageKey)) {
    return <Navigate to={allowedPages.has('/admin') ? '/admin' : '/'} replace />;
  }

  return <>{children}</>;
}
